/**
 * Kora-specific serialization: which sections are in scope, and the Kora-only
 * `send-window` shape. The generic draft and extension mechanics are shared
 * across config pages and live in `@/lib/utils/clinic-configuration`.
 */
import type { PatchOperation } from "@medplum/core";
import type { Basic, Extension } from "@medplum/fhirtypes";

import type { ConfigSchema, KoraConfig } from "@/types/clinic-configuration";
import { FHIR_EXTENSION_URLS } from "@/constants/fhir.constants";
import { DAY_CODES } from "@/constants/scheduling.constants";
import { clinicConfigurationSchema } from "@/lib/schemas";
import {
  buildSectionExtensions,
  fromSectionDraft,
  mergeSectionExtensions as mergeSharedSectionExtensions,
  toSectionDraft,
  toSectionDraftEntries,
} from "@/lib/utils/clinic-configuration";
import { isWeekSchedule } from "@/lib/utils/helpers/clinic-configuration";
import { toWireTime } from "@/lib/utils/helpers/scheduling";

export const toDraft = (model: KoraConfig.Model): KoraConfig.Draft => ({
  identity: toSectionDraft(
    clinicConfigurationSchema.identity.fields,
    model.identity
  ),
  tone: toSectionDraft(clinicConfigurationSchema.tone.fields, model.tone),
  escalation: toSectionDraft(
    clinicConfigurationSchema.escalation.fields,
    model.escalation
  ),
  followUps: toSectionDraftEntries(
    clinicConfigurationSchema.followUps.fields,
    model.followUps
  ),
  recommendedServices: toSectionDraft(
    clinicConfigurationSchema.recommendedServices.fields,
    model.recommendedServices
  ),
  scheduling: toSectionDraft(
    clinicConfigurationSchema.scheduling.fields,
    model.scheduling
  ),
  escalationConfiguration: toSectionDraft(
    clinicConfigurationSchema.escalationConfiguration.fields,
    model.escalationConfiguration
  ),
});

export const fromDraft = (
  draft: KoraConfig.Draft,
  model: KoraConfig.Model
): KoraConfig.Model => ({
  ...model,
  identity: fromSectionDraft(
    clinicConfigurationSchema.identity.fields,
    draft.identity
  ) as KoraConfig.Identity,
  tone: fromSectionDraft(
    clinicConfigurationSchema.tone.fields,
    draft.tone
  ) as KoraConfig.Tone,
  escalation: fromSectionDraft(
    clinicConfigurationSchema.escalation.fields,
    draft.escalation
  ) as KoraConfig.Escalation,
  followUps: draft.followUps.map(
    (entry) =>
      fromSectionDraft(
        clinicConfigurationSchema.followUps.fields,
        entry
      ) as KoraConfig.FollowUp
  ),
  recommendedServices: fromSectionDraft(
    clinicConfigurationSchema.recommendedServices.fields,
    draft.recommendedServices
  ) as KoraConfig.RecommendedServices,
  scheduling: fromSectionDraft(
    clinicConfigurationSchema.scheduling.fields,
    draft.scheduling
  ) as KoraConfig.Scheduling,
  escalationConfiguration: fromSectionDraft(
    clinicConfigurationSchema.escalationConfiguration.fields,
    draft.escalationConfiguration
  ) as KoraConfig.EscalationConfiguration,
});

/**
 * Flattens the week back into the wire's `(weekday, start-time, end-time)`
 * triples — the one field `buildSectionExtensions` leaves behind, since these
 * sub-slugs are Kora's own.
 *
 * A disabled day emits nothing, which is what makes an all-off week write no
 * `send-window` at all rather than seven empty ones.
 */
const buildSendWindowExtensions = (
  slug: string,
  schedule: unknown
): Extension[] => {
  if (!isWeekSchedule(schedule)) {
    return [];
  }

  return DAY_CODES.filter((day) => schedule[day].enabled).flatMap((day) =>
    schedule[day].ranges.map(({ start, end }) => ({
      url: slug,
      extension: [
        {
          url: FHIR_EXTENSION_URLS.KORA_CONFIG_SEND_WINDOW.WEEKDAY,
          valueCode: day,
        },
        {
          url: FHIR_EXTENSION_URLS.KORA_CONFIG_SEND_WINDOW.START_TIME,
          valueTime: toWireTime(start),
        },
        {
          url: FHIR_EXTENSION_URLS.KORA_CONFIG_SEND_WINDOW.END_TIME,
          valueTime: toWireTime(end),
        },
      ],
    }))
  );
};

const buildCompositeExtensions = (
  shape: ConfigSchema.ExtensionShape,
  values: Record<string, unknown>
): Extension[] =>
  Object.entries(shape)
    .filter(([, field]) => field.kind === "weekSchedule")
    .flatMap(([key, field]) =>
      buildSendWindowExtensions(field.slug, values[key])
    );

/**
 * Rebuilds one block's sub-extensions from a complete, validated draft for
 * it. For an `object` section the block is the section; for an `array` section
 * it is one entry. The draft must carry every writable key the section
 * declares: `buildKoraConfigPatchOperations` rebuilds the block from this
 * output alone, so a missing key is a deletion, not a no-op.
 *
 * The cast is needed because TypeScript cannot follow the section/draft
 * correlation once `BlockUpdate` is destructured; the union at the call site
 * is what makes it safe.
 */
export const buildKoraConfigExtensions = ({
  section,
  draft,
}: KoraConfig.BlockUpdate): Extension[] => {
  const shape = clinicConfigurationSchema[section].fields;
  const values = fromSectionDraft(
    shape,
    draft as ConfigSchema.SectionDraft<typeof shape>
  );
  return [
    ...buildSectionExtensions(shape, values),
    ...buildCompositeExtensions(shape, values),
  ];
};

/**
 * The shared merge, with Kora's block builder: Kora's sections hold the
 * `send-window` composite, which only this page knows how to write.
 *
 * Emits an `array` section's whole run where its first stored block sat, so a
 * save never reorders the resource: the run stays contiguous and in place, and
 * every extension after it keeps its index.
 */
const mergeSectionExtensions = (
  storedBlocks: Extension[],
  update: KoraConfig.SectionUpdate
): Extension[] =>
  mergeSharedSectionExtensions(
    storedBlocks,
    clinicConfigurationSchema[update.section],
    update.draft,
    (draft) =>
      buildKoraConfigExtensions({
        section: update.section,
        draft,
      } as KoraConfig.BlockUpdate)
  );

/**
 * Rebuilds every edited section and writes them in one patch: a `test` on
 * `/meta/versionId` plus a single `replace` of the whole `extension` array.
 *
 * `versionId` is the value the caller knew at page load — deliberately not
 * `basic.meta.versionId`, since `basic` may be a fresher fetch than what the
 * user was editing against. One `test` for all sections is also why they have
 * to travel together: a second patch would carry a versionId this one just
 * invalidated.
 *
 * The array is replaced whole rather than patched per block at
 * `/extension/{index}`, because positional paths computed from one snapshot
 * but applied in sequence break as soon as two sections need the same op.
 * Replacing whole keeps the op count at two however many sections are dirty,
 * and the `test` op rejects the patch if anything else wrote to the resource
 * after page load.
 */
export const buildKoraConfigPatchOperations = (
  basic: Basic,
  updates: KoraConfig.SectionUpdate[],
  versionId: string
): PatchOperation[] => {
  const extensions = basic.extension ?? [];
  const updateBySlug = new Map<string, KoraConfig.SectionUpdate>(
    updates.map((update) => [
      clinicConfigurationSchema[update.section].slug,
      update,
    ])
  );

  const storedBlocksBySlug = new Map<string, Extension[]>();
  for (const extension of extensions) {
    if (updateBySlug.has(extension.url)) {
      storedBlocksBySlug.set(extension.url, [
        ...(storedBlocksBySlug.get(extension.url) ?? []),
        extension,
      ]);
    }
  }

  /**
   * The rest of an edited section's run is dropped — `mergeSectionExtensions`
   * emits the whole run at the first stored block.
   */
  const emittedSlugs = new Set<string>();
  const rewritten = extensions.flatMap((extension) => {
    const update = updateBySlug.get(extension.url);
    if (!update) {
      return [extension];
    }
    if (emittedSlugs.has(extension.url)) {
      return [];
    }
    emittedSlugs.add(extension.url);
    return mergeSectionExtensions(
      storedBlocksBySlug.get(extension.url) ?? [],
      update
    );
  });

  /**
   * Sections with no block stored yet go after the existing ones, so a save
   * never reorders what is already on the resource.
   */
  const appended = updates
    .filter(
      ({ section }) =>
        !emittedSlugs.has(clinicConfigurationSchema[section].slug)
    )
    .flatMap((update) => mergeSectionExtensions([], update));

  const testOp: PatchOperation = {
    op: "test",
    path: "/meta/versionId",
    value: versionId,
  };

  const value = [...rewritten, ...appended];
  const extensionOp: PatchOperation = basic.extension
    ? { op: "replace", path: "/extension", value }
    : { op: "add", path: "/extension", value };

  return [testOp, extensionOp];
};
